/**
 * Centralized conversion event tracking for daily puzzle games.
 * 
 * This module provides automatic de-duplication of conversion events using the local save manager.
 * Games simply call reportConversionEvent() and the system handles tracking to prevent duplicates.
 */

import { reportEvent, getCurrentGameId } from "./Analytics";
import { EVENT_CATEGORIES, ANALYTICS_LOG_PREFIX } from "./AnalyticsConstants";
import { LocalSaveManager } from "../persistence/LocalSaveManager";
import { logger } from "./Logger";

const ANALYTICS_NAMESPACE = "analytics";
const COOKIE_NAME_CONVERSION_EVENT_TRACKING = "conversionEventTracking";
const FOREVER_DATE = new Date(3000, 1, 1);

/**
 * Record of a single conversion event emission
 */
interface ConversionEventRecord {
  eventName: string;
  timestamp: number;
  levelId?: string;
  metadata?: Record<string, any>;
}

/**
 * Storage structure for all conversion event tracking
 */
interface ConversionEventTracking {
  events: ConversionEventRecord[];
}

/**
 * Options for reporting conversion events
 */
export interface ReportConversionEventOptions {
  /** Optional: Associate event with a specific level/puzzle ID */
  levelId?: string;
  /** Optional: Additional metadata to store */
  metadata?: Record<string, any>;
  /** Optional: Only fire once per day (checks since midnight) */
  oncePerDay?: boolean;
  /** Optional: Only fire once per level (requires levelId) */
  oncePerLevel?: boolean;
}

/**
 * Load conversion event tracking for a game and clean up old events
 */
const loadConversionEventTracking = (): ConversionEventTracking => {
  const gameId = getCurrentGameId();
  const namespace = `${ANALYTICS_NAMESPACE}_${gameId}`;
  const saveObject = LocalSaveManager.loadSimpleData<ConversionEventTracking>(namespace, COOKIE_NAME_CONVERSION_EVENT_TRACKING);
  const tracking = saveObject?.data ?? { events: [] };
  
  // Clean up old events to prevent cookie bloat
  const now = Date.now();
  const twoDaysAgo = now - (2 * 24 * 60 * 60 * 1000);
  const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);
  
  tracking.events = tracking.events.filter((event: ConversionEventRecord) => {
    // Keep events from the last 2 days (for oncePerDay checks - handles edge cases like playing morning and then evening)
    if (event.timestamp > twoDaysAgo) return true;
    
    // Keep level-specific events from the last 30 days (for oncePerLevel checks)
    if (event.levelId && event.timestamp > thirtyDaysAgo) return true;
    
    // Discard everything else
    return false;
  });
  
  return tracking;
};

/**
 * Save conversion event tracking for a game
 */
const saveConversionEventTracking = (tracking: ConversionEventTracking): void => {
  const gameId = getCurrentGameId();
  const namespace = `${ANALYTICS_NAMESPACE}_${gameId}`;
  LocalSaveManager.saveSimpleData(namespace, COOKIE_NAME_CONVERSION_EVENT_TRACKING, tracking, FOREVER_DATE);
};

/**
 * Check if a conversion event has already been tracked
 */
const hasTrackedConversionEvent = (
  eventName: string,
  options?: {
    levelId?: string;
    sinceTimestamp?: number;
  }
): boolean => {
  const tracking = loadConversionEventTracking();
  
  return tracking.events.some(record => {
    if (record.eventName !== eventName) return false;
    if (options?.levelId && record.levelId !== options.levelId) return false;
    if (options?.sinceTimestamp && record.timestamp < options.sinceTimestamp) return false;
    return true;
  });
};

/**
 * Track that a conversion event was fired
 */
const trackConversionEvent = (
  eventName: string,
  options?: {
    levelId?: string;
    metadata?: Record<string, any>;
  }
): void => {
  const tracking = loadConversionEventTracking();
  
  tracking.events.push({
    eventName,
    timestamp: Date.now(),
    levelId: options?.levelId,
    metadata: options?.metadata
  });
  
  saveConversionEventTracking(tracking);
  logger.debug(`${ANALYTICS_LOG_PREFIX} Tracked conversion event: ${eventName}`, options);
};

/**
 * Reports a conversion event to Google Analytics with automatic de-duplication.
 * 
 * This function automatically prevents duplicate event emissions based on the provided options:
 * - oncePerDay: Event fires only once per day (since midnight)
 * - oncePerLevel: Event fires only once per specific level/puzzle (requires levelId)
 * - Neither option: Event fires every time (no de-duplication)
 * 
 * Uses Redux store to automatically get the current game - no need to pass it in!
 * 
 * @param eventName - The name of the conversion event (use EVENT_NAMES constants)
 * @param options - Optional configuration for de-duplication and tracking
 * @param label - Optional descriptive label for the event
 * 
 * @example
 * // No de-duplication (fires every time)
 * reportConversionEvent(EVENT_NAMES.BUTTON_CLICK_SHARE, {}, "User shared");
 * 
 * @example
 * // Fire once per day
 * reportConversionEvent(
 *   EVENT_NAMES.DISPLAY_DAILY_PUZZLES,
 *   { oncePerDay: true },
 *   "User viewing daily puzzle"
 * );
 * 
 * @example
 * // Fire once per puzzle
 * reportConversionEvent(
 *   EVENT_NAMES.INTERACT_WITH_DAILY_PUZZLES,
 *   { levelId: "puzzle_2024_12_01", oncePerLevel: true },
 *   "First move"
 * );
 * 
 * @example
 * // Fire once per puzzle with metadata
 * reportConversionEvent(
 *   EVENT_NAMES.COMPLETE_DAILY_PUZZLES,
 *   { levelId: "puzzle_2024_12_01", oncePerLevel: true, metadata: { won: true } },
 *   "won"
 * );
 */
export const reportConversionEvent = (
  eventName: string,
  options: ReportConversionEventOptions = {},
  label?: string
): void => {
  // Check if we should skip this event based on tracking options
  let shouldSkip = false;
  let checkOptions: { levelId?: string; sinceTimestamp?: number } = {};

  if (options.oncePerDay) {
    checkOptions.sinceTimestamp = new Date().setHours(0, 0, 0, 0);
  }

  if (options.oncePerLevel) {
    if (!options.levelId) {
      logger.error(`${ANALYTICS_LOG_PREFIX} oncePerLevel specified but no levelId provided for event: ${eventName}`);
    } else {
      checkOptions.levelId = options.levelId;
    }
  }

  // Only check if we have any tracking options
  if (options.oncePerDay || options.oncePerLevel) {
    shouldSkip = hasTrackedConversionEvent(eventName, checkOptions);
  }

  if (shouldSkip) {
    logger.debug(`${ANALYTICS_LOG_PREFIX} Conversion event already tracked, skipping: ${eventName}`, checkOptions);
    return;
  }

  // Fire the event
  reportEvent({
    action: eventName,
    category: EVENT_CATEGORIES.CONVERSION,
    label: label,
    value: 1
  });

  // Track that we fired it (if any tracking options were provided)
  if (options.oncePerDay || options.oncePerLevel) {
    trackConversionEvent(eventName, {
      levelId: options.levelId,
      metadata: options.metadata
    });
  }
};
